"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
const auth_1 = require("@aws-amplify/auth");
const core_utility_1 = require("@built/core-utility");
const errors_1 = require("./errors");
const errors_2 = require("../../errors");
const cookies_1 = require("./cognito/cookies");
const sso_1 = require("./cognito/sso");
const { error } = (0, core_utility_1.getScopedLogger)('built-cognito');
const providerError = (message, ex, data, code) => {
    error(message, ex);
    return Object.assign(Object.assign({}, (0, errors_2.errorResult)(code !== null && code !== void 0 ? code : 'PROVIDER_FAILURE', message, ex)), { data });
};
const notInitializedError = (data) => providerError('Adapter not initialized.', undefined, data);
const initOldAuth = async (config) => {
    // this is a temporary measure until Dragon apps are updated to use this new auth utility
    // it seems AWS Amplify stores config in memory so configuring it here doesn't configure it there
    try {
        const util = await System.import('@built/auth-utility');
        const { amplifyInit } = util;
        await amplifyInit((0, core_utility_1.removeUndefined)({
            region: config.authentication.region,
            userPoolId: config.authentication.userPoolId,
            userPoolWebClientId: config.authentication.userPoolWebClientId,
            cookieStorage: config.authentication.cookies,
        }));
    }
    catch (ex) {
        error('Could not initialize old auth utility.', ex);
    }
};
class CognitoAuthenticationAdapter {
    constructor() {
        this.name = 'cognito';
    }
    get initialized() {
        return this.config !== undefined;
    }
    /** Gets user pool Web client ID if logged in user is an SSO user. */
    getSsoClientId() {
        // This is to support discovering user's logged in SSO web client ID because it is different from the default one we configure per environment.
        const ssoIdentity = (0, sso_1.getSsoIdentity)();
        if (!ssoIdentity) {
            return;
        }
        return ssoIdentity.aud; // the audience maps to the client ID
    }
    validateConfig(config) {
        const { authentication } = config;
        if (!authentication.region) {
            authentication.region = 'us-east-1';
        }
        if (!authentication.userPoolId) {
            throw new core_utility_1.ConfigError('Missing userPoolId.');
        }
        authentication.userPoolWebClientId = this.getSsoClientId() || authentication.userPoolWebClientId;
        if (!authentication.userPoolWebClientId) {
            if (!authentication.userPoolWebClientId) {
                throw new core_utility_1.ConfigError('userPoolWebClientId not provided and could not be found in cookies.');
            }
        }
        const { cookies } = authentication;
        if (!cookies.domain) {
            throw new core_utility_1.ConfigError('Missing cookies domain.');
        }
    }
    async initialize(config) {
        this.givenAuthConfig = config.authentication;
        this.validateConfig(config);
        try {
            const { authentication } = (0, core_utility_1.removeUndefined)(config);
            this.awsAuthConfig = {
                region: authentication.region,
                userPoolId: authentication.userPoolId,
                userPoolWebClientId: authentication.userPoolWebClientId,
                cookieStorage: authentication.cookies,
            };
            auth_1.Auth.configure(this.awsAuthConfig);
            await initOldAuth(config);
            this.config = config;
        }
        catch (ex) {
            return providerError('Could not initialize authentication.', ex);
        }
        return {
            ok: true,
        };
    }
    getLogData() {
        var _a;
        const _b = (_a = this.givenAuthConfig) !== null && _a !== void 0 ? _a : {}, { logging } = _b, adapterConfig = __rest(_b, ["logging"]);
        const logData = Object.assign({ initialized: this.initialized, adapterConfig, awsConfig: this.awsAuthConfig }, (0, cookies_1.getCookieLogData)());
        return logData;
    }
    async getAuthToken() {
        if (!this.initialized) {
            return notInitializedError('');
        }
        try {
            const tokens = await auth_1.Auth.currentSession();
            const data = tokens.getIdToken().getJwtToken();
            return {
                ok: true,
                data,
            };
        }
        catch (e) {
            const cognitoCookies = (0, cookies_1.discoverCognitoCookies)();
            if (e.code === 'NotAuthorizedException' && /refresh/i.test(e.message)) {
                // this handles where we lost the session and refresh token cannot be used
                // error seen in the wild reports: 'Refresh Token has expired'
                // we want to handle this as NoAuthSession and so avoid reporting it
                return providerError('Session lost.', new errors_1.NoAuthSession(e.message), undefined, 'NO_AUTH_SESSION');
            }
            if (Object.keys(cognitoCookies).length === 0) {
                return providerError('Session not established.', new errors_1.NoAuthSession(), undefined, 'NO_AUTH_SESSION');
            }
            return providerError('Could not get auth token.', e);
        }
    }
    async getAuthTokenData() {
        if (!this.initialized) {
            return notInitializedError();
        }
        const tokenResult = await this.getAuthToken();
        if (!tokenResult.data) {
            return Object.assign(Object.assign({}, tokenResult), { data: undefined });
        }
        const tokenData = (0, core_utility_1.parseJwtPayload)(tokenResult.data);
        if (!tokenData) {
            return providerError('Could not parse auth token to get auth user.');
        }
        const user = {
            tokenUid: tokenData.sub,
            userUid: tokenData.sub,
            email: tokenData.email,
            lastLogin: tokenData.last_login,
            issueTime: tokenData.iat * 1000,
            expirationTime: tokenData.exp * 1000,
            rawToken: tokenResult.data,
        };
        return {
            ok: true,
            data: user,
        };
    }
    async changePassword(oldPassword, newPassword) {
        if (!this.initialized) {
            return notInitializedError();
        }
        try {
            const currentUser = await auth_1.Auth.currentAuthenticatedUser();
            await auth_1.Auth.changePassword(currentUser, oldPassword, newPassword);
        }
        catch (ex) {
            return providerError('Could not change password.', ex);
        }
        return {
            ok: true,
        };
    }
    async signOut() {
        if (!this.initialized) {
            return notInitializedError();
        }
        try {
            await auth_1.Auth.signOut();
        }
        catch (ex) {
            return providerError('Could not sign out.', ex);
        }
        return {
            ok: true,
        };
    }
    async refreshSession() {
        if (!this.initialized) {
            return notInitializedError();
        }
        const user = await auth_1.Auth.currentUserPoolUser();
        const oldSession = user.getSignInUserSession();
        const refreshToken = oldSession === null || oldSession === void 0 ? void 0 : oldSession.getRefreshToken();
        return new Promise((resolve) => {
            if (refreshToken) {
                user.refreshSession(refreshToken, (error) => {
                    if (error) {
                        return resolve(providerError('Could not refresh the session.', error));
                    }
                    resolve({
                        ok: true,
                    });
                });
            }
        });
    }
}
exports.default = CognitoAuthenticationAdapter;
